/**
 * Status eines Lebensbereichs (Roadmap F-01, V-01) - rein und getestet:
 * aktueller Lebensrad-Wert samt vorigem, offene Aufgaben, letzte Aktivitaet.
 */
import { differenceInDays, toLocalDate } from '@/lib/datetime';
import type {
  Book, Goal, Habit, HabitEntry, ID, LifeWheelSnapshot, LocalDate, Project, Task,
} from '@/domain/types';

export interface BereichsStatus {
  /** Juengste Lebensrad-Bewertung dieses Bereichs. */
  lebensrad?: number;
  /** Die Bewertung davor - fuer die Veraenderung. */
  vorher?: number;
  offeneAufgaben: number;
  /** Tag der letzten Aktivitaet, hoechstens heute. */
  letzteAktivitaet?: LocalDate;
}

export interface BereichsDaten {
  tasks: Task[];
  projects: Project[];
  goals: Goal[];
  habits: Habit[];
  habitEntries: HabitEntry[];
  books: Book[];
  snapshots: LifeWheelSnapshot[];
}

/** ISO-Zeitpunkt -> lokaler Kalendertag. */
const tagVon = (iso: string | undefined): LocalDate | undefined => {
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : toLocalDate(d);
};

export function bereichsStatus(areaId: ID, daten: BereichsDaten, heute: LocalDate): BereichsStatus {
  const bewertet = [...daten.snapshots]
    .filter((s) => typeof s.scores[areaId] === 'number' && s.date <= heute)
    .sort((a, b) => b.date.localeCompare(a.date));

  const eigeneAufgaben = daten.tasks.filter((t) => t.areaId === areaId);
  const eigeneGewohnheiten = new Set(daten.habits.filter((h) => h.areaId === areaId).map((h) => h.id));

  const tage: (LocalDate | undefined)[] = [
    ...eigeneAufgaben.map((t) => tagVon(t.completedAt ?? t.updatedAt)),
    ...daten.projects.filter((p) => p.areaId === areaId).map((p) => tagVon(p.updatedAt)),
    ...daten.goals.filter((g) => g.areaId === areaId).map((g) => tagVon(g.updatedAt)),
    ...daten.books.filter((b) => b.areaIds.includes(areaId)).map((b) => tagVon(b.updatedAt)),
    ...daten.habitEntries.filter((e) => e.done && eigeneGewohnheiten.has(e.habitId)).map((e) => e.date),
    ...bewertet.slice(0, 1).map((s) => s.date),
  ];
  const letzte = tage.filter((d): d is LocalDate => Boolean(d) && d! <= heute).sort().at(-1);

  return {
    lebensrad: bewertet[0]?.scores[areaId],
    vorher: bewertet[1]?.scores[areaId],
    offeneAufgaben: eigeneAufgaben.filter((t) => t.status !== 'erledigt').length,
    ...(letzte ? { letzteAktivitaet: letzte } : {}),
  };
}

/** "heute", "gestern", "vor 3 Tagen", "vor 2 Wochen", "vor 3 Monaten". */
export function wannText(datum: LocalDate | undefined, heute: LocalDate): string {
  if (!datum) return 'noch nichts';
  const tage = differenceInDays(datum, heute);
  if (tage <= 0) return 'heute';
  if (tage === 1) return 'gestern';
  if (tage < 14) return `vor ${tage} Tagen`;
  if (tage < 60) return `vor ${Math.floor(tage / 7)} Wochen`;
  return `vor ${Math.floor(tage / 30)} Monaten`;
}
